/**
 * GlobalView — Auth Service
 *
 * Thin wrapper around Firebase Auth for Google Sign-In (§25.2).
 * Decoupled from UI — consumed by stores and guards.
 */

import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  type User,
  type Unsubscribe,
} from 'firebase/auth';
import { auth } from './firebase';

const googleProvider = new GoogleAuthProvider();

/**
 * Sign in with Google via Firebase Auth popup.
 * Returns the authenticated Firebase User.
 */
export async function signInWithGoogle(): Promise<User> {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

/**
 * Sign out the current user.
 */
export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

/**
 * Subscribe to auth state changes.
 * @returns Unsubscribe function.
 */
export function onAuthChange(callback: (user: User | null) => void): Unsubscribe {
  return firebaseOnAuthStateChanged(auth, callback);
}

/**
 * Get the current user's ID token for API calls.
 * @param forceRefresh - Force token refresh to get latest custom claims.
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

/**
 * Get the current user's token result including custom claims.
 */
export async function getTokenClaims(): Promise<Record<string, unknown> | null> {
  const user = auth.currentUser;
  if (!user) return null;
  const result = await user.getIdTokenResult(true);
  return result.claims as Record<string, unknown>;
}
